import { useState, useEffect, type ReactNode } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { createPortal } from 'react-dom'
import { useMobileNavStore } from '../store/mobileNav'
import { PexIcon } from './icons/PexIcon'
import type { PexIconName } from './icons/pex-icon-registry'
import { useT } from '../i18n'
import { sharedUiI18n } from '../i18n/components/shared-ui'
import { SidebarCollapsedContext } from './sidebarCollapsedContext'

const SIDEBAR_COLLAPSED_KEY = 'pexify_sidebar_collapsed_v1'

const sidebarItemBase =
  'relative flex min-h-11 lg:min-h-[var(--sidebar-item-height)] w-full items-center gap-[var(--sidebar-item-gap)] rounded-[var(--sidebar-item-radius)] px-[var(--sidebar-item-padding-x)] text-[length:var(--sidebar-item-font-size)] transition-colors focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)]'

const sidebarItemActive =
  'bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-text)] font-[var(--sidebar-item-active-font-weight)] shadow-[var(--sidebar-active-shadow)] before:absolute before:inset-y-[var(--sidebar-active-indicator-inset)] before:-left-[var(--sidebar-nav-inset)] before:w-[var(--sidebar-active-indicator-width)] before:rounded-full before:bg-[var(--sidebar-active-indicator)]'

// Hierarchy lives in two constants, not at each call site: a parent row is
// heavier and darker than the pages nested under it. Levels 2 and 3 share the
// child treatment on purpose — a third tier of contrast would be noise.
const sidebarItemParent = `${sidebarItemBase} font-[var(--sidebar-parent-font-weight)]`

const sidebarItemChild = `${sidebarItemBase} font-[var(--sidebar-child-font-weight)]`

const sidebarItemIdle =
  'text-[var(--sidebar-parent-text)] hover:bg-[var(--sidebar-hover-bg)] hover:text-[var(--sidebar-active-text)]'

const sidebarChildIdle =
  'text-[var(--sidebar-child-text)] hover:bg-[var(--sidebar-hover-bg)] hover:text-[var(--sidebar-active-text)]'

const sidebarSectionLabel =
  'text-[length:var(--sidebar-section-font-size)] font-[var(--sidebar-section-font-weight)] uppercase tracking-[var(--sidebar-section-tracking)] text-[var(--sidebar-count)]'

// A third nav level: an expandable child (e.g. a catalog category) can carry its
// own leaf sub-items (e.g. subcategories). Purely additive — a child with no
// subItems renders exactly as before.
export type SidebarSubItem = {
  to: string
  label: string
  badge?: number
  // Optional muted qualifier after the label (e.g. the org a shop belongs to on
  // the admin store breakdown). Truncates with the label, never wraps.
  hint?: string
}

export type SidebarItem = {
  to: string
  label: string
  icon?: PexIconName
  // Stable semantic hook for contextual product guidance. Kept separate from
  // localized labels and presentation classes so tours survive copy/design changes.
  tourTarget?: string
  external?: boolean
  isHeader?: boolean
  // Parent submenu: groupId acts as collapsible parent label.
  // Children declare parentGroupId pointing back to the group's groupId.
  groupId?: string
  parentGroupId?: string
  // Optional count pill rendered right-aligned next to the label (ShipStation
  // style). Shown only when > 0.
  badge?: number
  // Non-clickable placeholder with a "soon" pill (e.g. Workspace surfaces not
  // yet wired on a given shell). Rendered as a muted, inert row.
  disabled?: boolean
  // Third-level leaves under a child row (catalog subcategories). When present,
  // the child renders an expand caret and toggles these open.
  subItems?: SidebarSubItem[]
  // Other routes that should also light up this row — the sibling screens of
  // a route-tab group reached through one menu entry (e.g. SKU Mapping's
  // four tabs). Each path matches like `to` does.
  alsoActiveFor?: string[]
}

function isLinkActive(itemTo: string, pathname: string, search: string) {
  // Strip query/hash from item's `to` for path comparison.
  const [itemPath, itemQuery] = itemTo.split('?')
  const pathMatch =
    pathname === itemPath || pathname.startsWith(itemPath + '/')
  if (!pathMatch) return false
  if (!itemQuery) {
    // For path-only items, require search to ALSO be empty so query-bearing
    // siblings (e.g. /admin/orders?status=on_hold) win over the plain entry.
    return search === '' || search === '?'
  }
  // For query-bearing items, do exact query string match.
  const normalize = (s: string) => s.replace(/^\?/, '')
  return normalize(search) === normalize(itemQuery)
}

export function Sidebar({
  title,
  items,
  footer,
  header,
  headerIcon = 'organization',
  layout = 'default',
}: {
  title: string
  items: SidebarItem[]
  footer?: ReactNode
  // Optional block rendered at the very top of the sidebar (above the nav), in
  // both the desktop sidebar and the mobile drawer. Used for the logo +
  // role-specific selector (Reibul-style split shell).
  header?: ReactNode
  headerIcon?: PexIconName
  layout?: 'default' | 'aligned'
}) {
  const t = useT(sharedUiI18n).sidebar
  const aligned = layout === 'aligned'
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      // Layout remains usable when browser storage is unavailable.
    }
  }, [collapsed])
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const search = useRouterState({ select: (s) => s.location.searchStr ?? '' })

  // Length of the longest path of this item that matches the current
  // location; 0 = no match. Its own `to` matches with the usual query rules;
  // alsoActiveFor routes match on the path alone, so a sibling tab keeps the
  // row lit while its own filters sit in the query string.
  const matchLength = (it: SidebarItem) =>
    Math.max(
      0,
      ...(isLinkActive(it.to, pathname, search) ? [it.to.length] : []),
      ...(it.alsoActiveFor ?? [])
        .filter((to) => pathname === to || pathname.startsWith(to + '/'))
        .map((to) => to.length),
    )

  // Best-match active resolution across non-header / non-external items.
  const activeTo = items
    .filter((it) => !it.isHeader && !it.external && matchLength(it) > 0)
    .sort((a, b) => matchLength(b) - matchLength(a))[0]?.to

  // The group (if any) holding the active route — a child row, or one of a
  // child's third-level rows (e.g. a store under Awaiting Shipment).
  const activeGroupId = items.find(
    (it) =>
      it.parentGroupId &&
      !it.external &&
      (matchLength(it) > 0 ||
        (it.subItems ?? []).some((sub) => isLinkActive(sub.to, pathname, search))),
  )?.parentGroupId

  // Track expanded state per groupId. The group holding the route you land on
  // starts open — otherwise the first visit to e.g. My Orders showed its
  // section collapsed, because the change-detection below only fires when the
  // active group CHANGES after mount.
  const groupIds = items.filter((it) => it.groupId).map((it) => it.groupId!)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    for (const gid of groupIds) initial[gid] = gid === activeGroupId
    return initial
  })

  // Auto-expand the active group ONLY when the route lands in a new group —
  // render-time reset (React's "adjust state on change" pattern, no effect).
  // Manual collapse then persists until the active group changes again.
  // (The previous every-render effect snapped a manually-collapsed active group
  // back open, so the user could never close it.)
  const [prevActiveGroup, setPrevActiveGroup] = useState<string | undefined>(activeGroupId)
  if (activeGroupId !== prevActiveGroup) {
    setPrevActiveGroup(activeGroupId)
    if (activeGroupId) {
      setExpandedGroups((prev) =>
        prev[activeGroupId] ? prev : { ...prev, [activeGroupId]: true },
      )
    }
  }

  const toggleGroup = (gid: string) =>
    setExpandedGroups((prev) => ({ ...prev, [gid]: !prev[gid] }))
  const expandGroup = (gid: string) =>
    setExpandedGroups((prev) => prev[gid] ? prev : { ...prev, [gid]: true })
  const openCompactGroup = (gid: string) => {
    if (collapsed && window.innerWidth >= 1024) {
      setCollapsed(false)
      expandGroup(gid)
      return true
    }
    return false
  }
  const compactItemClass = collapsed ? ' lg:justify-center lg:px-0' : ''
  const compactLabelClass = collapsed ? ' lg:sr-only' : ''
  const [railTooltip, setRailTooltip] = useState<{ label: string; left: number; top: number } | null>(null)
  const showRailTooltip = (target: EventTarget | null) => {
    if (!collapsed || window.innerWidth < 1024 || !(target instanceof Element)) return
    const control = target.closest<HTMLElement>('[data-rail-label]')
    const label = control?.dataset.railLabel
    if (!control || !label) return
    const rect = control.getBoundingClientRect()
    setRailTooltip({ label, left: rect.right + 10, top: rect.top + rect.height / 2 })
  }
  const clearRailTooltip = (target: EventTarget | null, next: EventTarget | null) => {
    if (!(target instanceof Element)) return
    const control = target.closest('[data-rail-label]')
    const nextControl = next instanceof Element ? next.closest('[data-rail-label]') : null
    if (control && control !== nextControl) setRailTooltip(null)
  }

  // Third-level (subcategory) expand state, keyed by the child's `to`. Manual —
  // the child row itself navigates; the caret is a separate toggle affordance.
  const [expandedChildren, setExpandedChildren] = useState<Record<string, boolean>>({})
  const toggleChild = (key: string) =>
    setExpandedChildren((prev) => ({ ...prev, [key]: !prev[key] }))
  const isSubActive = (to: string) => isLinkActive(to, pathname, search)

  // Mobile drawer state — sidebar renders inline on lg+ (>=1024px) and as a
  // slide-over drawer below. Auto-close on route change keeps the drawer
  // out of the way after navigation.
  const sidebarOpen = useMobileNavStore((s) => s.sidebarOpen)
  const closeSidebar = useMobileNavStore((s) => s.closeSidebar)

  useEffect(() => {
    closeSidebar()
  }, [pathname, closeSidebar])

  return (
    <SidebarCollapsedContext.Provider value={collapsed}>
    <>
      {/* Mobile scrim — visible only when drawer is open + <lg. Click to close. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-[rgb(23_23_23_/_0.4)] lg:hidden animate-scrim-in"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}
      <aside
        className={
          // Mobile (<lg): fixed slide-over drawer with safe-area awareness.
          // Desktop (lg+): inline static sidebar (original behavior).
          // Keep long labels from creating a horizontal rail scrollbar.
        'border-r border-[var(--sidebar-divider)] bg-[var(--sidebar-bg)] px-[var(--sidebar-nav-inset)] pt-0 text-[var(--sidebar-parent-text)] ' +
          (aligned ? 'flex flex-col overflow-hidden pb-2 ' : 'overflow-x-hidden overflow-y-auto pb-4 ') +
          'fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] safe-top safe-bottom ' +
          // Desktop (lg+): inline sidebar; default shells scroll the rail,
          // while the Seller-aligned layout scrolls only the menu.
          'lg:static lg:max-w-none lg:shrink-0 lg:safe-top-0 ' +
          (collapsed
            ? 'lg:w-[var(--sidebar-width-collapsed)] '
            : 'lg:w-[var(--sidebar-width)] ') +
          (sidebarOpen
            ? 'translate-x-0 animate-drawer-in shadow-2xl'
            : '-translate-x-full lg:translate-x-0')
        }
        aria-label={t.primaryNavigation}
        data-collapsed={collapsed ? 'true' : 'false'}
        onFocusCapture={(event) => showRailTooltip(event.target)}
        onBlurCapture={(event) => clearRailTooltip(event.target, event.relatedTarget)}
        onMouseOver={(event) => showRailTooltip(event.target)}
        onMouseOut={(event) => clearRailTooltip(event.target, event.relatedTarget)}
      >
        {/* Mobile close row — drawer mode only. In normal flow (not absolute)
            so it can never overlap the shell header content below it. */}
        <div className="sticky top-0 z-10 -mx-[var(--sidebar-nav-inset)] flex justify-end bg-[var(--sidebar-bg)] px-2 pt-2 lg:hidden">
          <button
            type="button"
            onClick={closeSidebar}
            className="z-10 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[var(--radius-control)] p-2 hover:bg-[var(--sidebar-hover-bg)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)]"
            aria-label={t.closeNavigation}
          >
            <PexIcon name="close" size="md" tone="navigation" />
          </button>
        </div>
      {header && (
        <>
          <div className={`-mx-[var(--sidebar-nav-inset)] mb-2 ${collapsed ? 'lg:hidden' : ''}`}>{header}</div>
          {collapsed && (
            <div className="hidden justify-center border-b border-[var(--sidebar-divider)] pb-2 lg:flex">
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                aria-label={t.showContextSelector}
                title={t.showContextSelector}
                data-rail-label={t.showContextSelector}
                className="flex h-11 w-11 items-center justify-center rounded-[var(--sidebar-item-radius)] text-[var(--sidebar-icon)] hover:bg-[var(--sidebar-hover-bg)] hover:text-[var(--sidebar-parent-text)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)]"
              >
                <PexIcon name={headerIcon} size="nav" tone="navigation" />
              </button>
            </div>
          )}
        </>
      )}
      {/* Section title row also carries the desktop collapse toggle, so the
          rail has no empty strip above the context selector. */}
      <div className={`flex items-center justify-between gap-[var(--space-2)] pl-[var(--sidebar-item-padding-x)] pb-[var(--space-2)] pt-[var(--space-3)] ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}>
        <span className={`${sidebarSectionLabel} min-w-0 truncate ${collapsed ? 'lg:sr-only' : ''}`}>{title}</span>
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? t.expandNavigation : t.collapseNavigation}
          title={collapsed ? t.expandNavigation : t.collapseNavigation}
          data-rail-label={collapsed ? t.expandNavigation : undefined}
          className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-[var(--sidebar-item-radius)] border border-[var(--sidebar-divider)] bg-[var(--sidebar-control-bg)] text-[var(--sidebar-parent-text)] hover:bg-[var(--sidebar-control-hover-bg)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)] lg:flex"
        >
          <PexIcon name={collapsed ? 'caretRight' : 'caretLeft'} size="sm" tone="inherit" />
        </button>
      </div>
      <nav className={`sidebar-nav-scroll flex flex-col gap-0 ${aligned ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2' : 'pb-16'}`}>
        {items.map((item) => {
          // Skip rendering children inline; they render under their parent group.
          if (item.parentGroupId) return null

          if (item.isHeader) {
            // Empty label = visual divider (e.g. Settings separator at bottom of shells).
            if (!item.label) {
              return (
                <div
                  key={`header:divider:${item.to}`}
                  className="mx-3 my-2 border-t border-[var(--sidebar-divider)]"
                />
              )
            }
            return (
              <div
                key={`header:${item.label}`}
                className={`${sidebarSectionLabel} mt-[var(--space-2)] px-[var(--sidebar-item-padding-x)] pb-[var(--space-1)] pt-[var(--space-1)] ${collapsed ? 'lg:mx-2 lg:my-2 lg:h-px lg:border-t lg:border-[var(--sidebar-divider)] lg:px-0 lg:py-0' : ''}`}
              >
                <span className={collapsed ? 'lg:sr-only' : ''}>{item.label}</span>
              </div>
            )
          }

          // Parent group with collapsible children.
          if (item.groupId) {
            const Icon = item.icon
            const isExpanded = expandedGroups[item.groupId] ?? false
            const children = items.filter((it) => it.parentGroupId === item.groupId)
            const Caret = isExpanded ? 'caretDown' : 'caretRight'
            // A navigable group head (one whose `to` is a real route) is itself
            // a destination, so it highlights when the route lands on it — not
            // only when one of its children is active. Without this, landing on
            // a group's own default view leaves nothing selected in the sidebar.
            const isGroupActive =
              item.to === activeTo || children.some((child) => child.to === activeTo)
            const groupRowClass =
              sidebarItemParent + ' ' + (isGroupActive ? sidebarItemActive : sidebarItemIdle) + compactItemClass
            const disclosureButton = (
              <button
                type="button"
                onClick={() => toggleGroup(item.groupId!)}
                aria-label={isExpanded ? t.collapseItem(item.label) : t.expandItem(item.label)}
                aria-expanded={isExpanded}
                className={`${aligned ? '-mr-[var(--sidebar-item-padding-x)]' : '-ml-[var(--sidebar-item-padding-x)]'} flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--sidebar-item-radius)] hover:bg-[var(--sidebar-hover-bg)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)] ${collapsed ? 'lg:hidden' : ''}`}
              >
                <PexIcon name={Caret} size="xs" tone="navigation" />
              </button>
            )
            return (
              <div key={`group:${item.groupId}`}>
                {item.to.includes('_group') ? (
                  <button
                    type="button"
                    data-tour={item.tourTarget}
                    onClick={() => {
                      if (!openCompactGroup(item.groupId!)) toggleGroup(item.groupId!)
                    }}
                    aria-expanded={isExpanded}
                    aria-label={collapsed ? item.label : undefined}
                    title={collapsed ? item.label : undefined}
                    data-rail-label={collapsed ? item.label : undefined}
                    className={groupRowClass}
                  >
                    {!aligned && <span data-disclosure-icon className={collapsed ? 'lg:hidden' : ''}><PexIcon name={Caret} size="xs" tone="navigation" /></span>}
                    {Icon && (
                      <PexIcon
                        name={Icon}
                        size="nav"
                        tone={isGroupActive ? 'navigation-active' : 'navigation'}
                      />
                    )}
                    <span className={`${aligned ? 'min-w-0 truncate' : ''} flex-1 text-left${compactLabelClass}`}>{item.label}</span>
                    {aligned && <span className={collapsed ? 'lg:hidden' : ''}><PexIcon name={Caret} size="xs" tone="navigation" /></span>}
                  </button>
                ) : (
                  <div className={groupRowClass}>
                    {!aligned && disclosureButton}
                    <Link
                      to={item.to}
                      data-tour={item.tourTarget}
                      onClick={(event) => {
                        if (openCompactGroup(item.groupId!)) event.preventDefault()
                        else expandGroup(item.groupId!)
                      }}
                      aria-label={collapsed ? item.label : undefined}
                      title={collapsed ? item.label : undefined}
                      data-rail-label={collapsed ? item.label : undefined}
                      className={`flex min-h-11 min-w-0 flex-1 items-center gap-[var(--sidebar-item-gap)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)] ${collapsed ? 'lg:justify-center' : ''}`}
                    >
                      {Icon && (
                        <PexIcon
                          name={Icon}
                          size="nav"
                          tone={isGroupActive ? 'navigation-active' : 'navigation'}
                        />
                      )}
                      <span className={`min-w-0 flex-1 truncate text-left${compactLabelClass}`}>{item.label}</span>
                    </Link>
                    {aligned && disclosureButton}
                  </div>
                )}
                {isExpanded && (
                  <div className={`ml-4 mt-0.5 flex flex-col gap-px border-l border-[var(--sidebar-divider)] pl-2 ${collapsed ? 'lg:hidden' : ''}`}>
                    {children.map((child) => {
                      const ChildIcon = child.icon
                      if (child.external) {
                        return (
                          <a
                            key={child.to}
                            href={child.to}
                            data-tour={child.tourTarget}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${sidebarItemChild} ${sidebarChildIdle}`}
                          >
                            {ChildIcon && (
                              <PexIcon name={ChildIcon} size="nav" tone="navigation" />
                            )}
                            <span>{child.label}</span>
                            <span className="ml-auto text-[10px] uppercase tracking-wider text-[var(--sidebar-count)]">
                              ↗
                            </span>
                          </a>
                        )
                      }
                      const isChildActive = child.to === activeTo
                      // Child with third-level subcategories: caret toggles the
                      // subs; the label still navigates to the category itself.
                      if (child.subItems && child.subItems.length > 0) {
                        const anySubActive = child.subItems.some((s) => isSubActive(s.to))
                        const expanded = expandedChildren[child.to] ?? anySubActive
                        const SubCaret = expanded ? 'caretDown' : 'caretRight'
                        return (
                          <div key={child.to}>
                            <div
                              className={
                                'relative flex min-h-11 lg:min-h-[var(--sidebar-item-height)] items-center rounded-[var(--sidebar-item-radius)] pr-1 text-[length:var(--sidebar-item-font-size)] font-[var(--sidebar-child-font-weight)] transition-colors ' +
                                (isChildActive
                                  ? sidebarItemActive
                                  : sidebarChildIdle)
                              }
                            >
                              <button
                                type="button"
                                onClick={() => toggleChild(child.to)}
                                aria-label={expanded ? t.collapse : t.expand}
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--sidebar-item-radius)] hover:bg-[var(--sidebar-hover-bg)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)]"
                              >
                                <PexIcon name={SubCaret} size="xs" tone="navigation" />
                              </button>
                              <Link
                                to={child.to}
                                data-tour={child.tourTarget}
                                className="flex flex-1 items-center gap-2 py-[4px] pr-1"
                              >
                                <span className="flex-1">{child.label}</span>
                                {typeof child.badge === 'number' && child.badge > 0 ? (
                                  <span
                                    className={
                                      'ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ' +
                                      (isChildActive
                                        ? 'bg-[var(--sidebar-active-icon)] text-[var(--sidebar-signal-foreground)]'
                                        : 'bg-[var(--sidebar-badge-bg)] text-[var(--sidebar-badge-text)]')
                                    }
                                  >
                                    {child.badge}
                                  </span>
                                ) : null}
                              </Link>
                            </div>
                            {expanded && (
                              <div className="ml-5 flex flex-col gap-px border-l border-[var(--sidebar-divider)] pl-2">
                                {child.subItems.map((sub) => {
                                  const subActive = isSubActive(sub.to)
                                  return (
                                    <Link
                                      key={sub.to}
                                      to={sub.to}
                                      title={sub.hint ? `${sub.label} · ${sub.hint}` : sub.label}
                                      className={
                                        // Same size and weight as the child rows
                                        // above; a long name truncates rather
                                        // than wrapping, so rows keep one height.
                                        sidebarItemChild +
                                        ' ' +
                                        (subActive
                                          ? sidebarItemActive
                                          : sidebarChildIdle)
                                      }
                                    >
                                      <span className="min-w-0 flex-1 truncate">
                                        {sub.label}
                                        {sub.hint ? (
                                          <span className="ml-1 text-[10px] text-[var(--sidebar-count)]">
                                            {sub.hint}
                                          </span>
                                        ) : null}
                                      </span>
                                      {typeof sub.badge === 'number' && sub.badge > 0 ? (
                                        <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--sidebar-badge-bg)] px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-[var(--sidebar-badge-text)]">
                                          {sub.badge}
                                        </span>
                                      ) : null}
                                    </Link>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      }
                      return (
                        <Link
                          key={child.to}
                          to={child.to}
                          data-tour={child.tourTarget}
                          className={
                            sidebarItemChild +
                            ' ' +
                            (isChildActive
                              ? sidebarItemActive
                              : sidebarChildIdle)
                          }
                        >
                          {ChildIcon && (
                            <PexIcon
                              name={ChildIcon}
                              size="nav"
                              tone={isChildActive ? 'navigation-active' : 'navigation'}
                            />
                          )}
                          <span>{child.label}</span>
                          {typeof child.badge === 'number' && child.badge > 0 ? (
                            <span
                              className={
                                'ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ' +
                                (isChildActive
                                  ? 'bg-[var(--sidebar-active-icon)] text-[var(--sidebar-signal-foreground)]'
                                  : 'bg-[var(--sidebar-badge-bg)] text-[var(--sidebar-badge-text)]')
                              }
                            >
                              {child.badge}
                            </span>
                          ) : null}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }

          const Icon = item.icon

          if (item.disabled) {
            return (
              <div
                key={item.to}
                className={`${sidebarItemParent} ${sidebarItemIdle} cursor-not-allowed select-none opacity-50${compactItemClass}`}
                aria-disabled="true"
                title={collapsed ? `${item.label} · ${t.comingSoon}` : t.comingSoon}
                data-rail-label={collapsed ? `${item.label} · ${t.comingSoon}` : undefined}
              >
                {!aligned && <span data-disclosure-gutter className={`h-4 w-4 shrink-0 ${collapsed ? 'lg:hidden' : ''}`} aria-hidden="true" />}
                {Icon && <PexIcon name={Icon} size="nav" tone="navigation" />}
                <span className={compactLabelClass}>{item.label}</span>
                <span className={`ml-auto rounded-full bg-[var(--sidebar-badge-bg)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--sidebar-badge-text)] ${collapsed ? 'lg:hidden' : ''}`}>
                  {t.soon}
                </span>
              </div>
            )
          }

          if (item.external) {
            return (
              <a
                key={item.to}
                href={item.to}
                data-tour={item.tourTarget}
                target="_blank"
                rel="noopener noreferrer"
                className={`${sidebarItemParent} ${sidebarItemIdle}${compactItemClass}`}
                aria-label={collapsed ? item.label : undefined}
                title={collapsed ? item.label : undefined}
                data-rail-label={collapsed ? item.label : undefined}
              >
                {/* Disclosure gutter — aligns leaf icons with expandable rows' icons. */}
                {!aligned && <span data-disclosure-gutter className={`h-4 w-4 shrink-0 ${collapsed ? 'lg:hidden' : ''}`} aria-hidden="true" />}
                {Icon && <PexIcon name={Icon} size="nav" tone="navigation" />}
                <span className={compactLabelClass}>{item.label}</span>
                <span className={`ml-auto text-[10px] uppercase tracking-wider text-[var(--sidebar-count)] ${collapsed ? 'lg:hidden' : ''}`}>
                  ↗
                </span>
              </a>
            )
          }

          const isActive = item.to === activeTo
          return (
            <Link
              key={item.to}
              to={item.to}
              data-tour={item.tourTarget}
              className={
                sidebarItemParent +
                ' ' +
                (isActive ? sidebarItemActive : sidebarItemIdle) + compactItemClass
              }
              aria-label={collapsed ? (typeof item.badge === 'number' && item.badge > 0 ? `${item.label} · ${t.unread(item.badge)}` : item.label) : undefined}
              title={collapsed ? item.label : undefined}
              data-rail-label={collapsed ? item.label : undefined}
            >
              {/* Disclosure gutter — aligns leaf icons with expandable rows' icons. */}
              {!aligned && <span data-disclosure-gutter className={`h-4 w-4 shrink-0 ${collapsed ? 'lg:hidden' : ''}`} aria-hidden="true" />}
              {Icon && (
                <PexIcon
                  name={Icon}
                  size="nav"
                  tone={isActive ? 'navigation-active' : 'navigation'}
                />
              )}
              <span className={compactLabelClass}>{item.label}</span>
              {typeof item.badge === 'number' && item.badge > 0 ? (
                <span
                  aria-label={collapsed ? undefined : t.unread(item.badge)}
                  aria-hidden={collapsed ? true : undefined}
                  className={
                    'ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ' +
                    (collapsed ? 'lg:absolute lg:right-1 lg:top-1 lg:ml-0 lg:h-2 lg:w-2 lg:min-w-0 lg:px-0 lg:py-0 lg:text-[0px] ' : '') +
                    (isActive
                      ? 'bg-[var(--sidebar-active-icon)] text-[var(--sidebar-signal-foreground)]'
                      : 'bg-[var(--sidebar-badge-bg)] text-[var(--sidebar-badge-text)]')
                  }
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </Link>
          )
        })}
      </nav>
      {footer && (
        <div className={`${aligned ? 'shrink-0' : 'sticky bottom-0'} mt-2 border-t border-[var(--sidebar-divider)] bg-[var(--sidebar-bg)]`}>
          {footer}
        </div>
      )}
    </aside>
    {collapsed && railTooltip && createPortal(
      <div
        role="tooltip"
        style={{ left: railTooltip.left, top: railTooltip.top }}
        className="pointer-events-none fixed z-[var(--z-dropdown)] -translate-y-1/2 rounded-[var(--radius-control)] border border-[var(--sidebar-divider)] bg-[var(--sidebar-bg)] px-2.5 py-1.5 text-xs font-medium text-[var(--sidebar-parent-text)] shadow-[var(--shadow-float)]"
      >
        {railTooltip.label}
      </div>,
      document.body,
    )}
    </>
    </SidebarCollapsedContext.Provider>
  )
}
